import React, { useCallback, useState, useEffect } from 'react';
import { orderGenerationFailuresApi } from '@/services/api';
import { ClientOrderGenerationFailureInfo } from '@/types/client';
import { formatDate } from '@/utils/formatters';
import { ColumnDef } from '@/components/utils/DataTable';
import DataTable from '@/components/utils/DataTable';
import { DateRangeFilter, loadCompanyOptions, FilterOption } from '@/components/utils/filters';
import { FilterSelect, FilterAsyncSelect } from '@/components/ui';
import { useTableState } from '@/hooks/useTableState';
import styles from '@/components/utils/shared.module.css';
import { useClientApi } from "@/hooks/useClientApi";

const ORDER_TYPE_OPTIONS: FilterOption[] = [
  { value: '1', label: 'Buy Order' },
  { value: '2', label: 'Sell Order' },
];

const PAGE_SIZE = 20;

const columns: ColumnDef<ClientOrderGenerationFailureInfo>[] = [
  {
    key: 'created_ts',
    header: 'Date',
    sortable: true,
    render: row => formatDate(row.created_at),
    width: '120px',
  },
  {
    key: 'client_master__client_code',
    header: 'Client Code',
    sortable: true,
    render: row => row.client_code,
    width: '120px',
  },
  {
    key: 'client_name',
    header: 'Client Name',
    sortable: false,
    render: row => row.client_name,
  },
  {
    key: 'order_type',
    header: 'Order Type',
    sortable: true,
    render: row => row.order_type_display,
    width: '120px',
  },
  {
    key: 'order_id',
    header: 'Order ID',
    sortable: false,
    render: row => row.order_id,
    width: '100px',
  },
  {
    key: 'company_name',
    header: 'Company',
    sortable: false,
    render: row => row.company_name || '—',
  },
  {
    key: 'reason',
    header: 'Failure Reason',
    sortable: false,
    render: row => row.reason,
  },
];

const OrderGenerationFailures: React.FC = () => {
  const { getClients, getClientFilter } = useClientApi();

  const loadClientOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
    try {
      const response = await getClients({ search: inputValue, page_size: 500 });
      return response.results.map(c => ({
        value: c.id.toString(),
        label: `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`}`,
      }));
    } catch {
      return [];
    }
  }, [getClients]);
  const [failures, setFailures] = useState<ClientOrderGenerationFailureInfo[]>([]);
  // Starts true: DateRangeFilter fills in the default dates asynchronously and the
  // first fetch follows, so the table shows the loader (not "No data") meanwhile.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<FilterOption | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<FilterOption | null>(null);
  const [selectedOrderType, setSelectedOrderType] = useState<FilterOption | null>(null);
  const [reasonOptions, setReasonOptions] = useState<FilterOption[]>([]);
  const [selectedReason, setSelectedReason] = useState<FilterOption | null>(null);

  const {
    currentPage,
    totalPages,
    totalCount,
    sortField,
    sortDir,
    searchQuery,
    debouncedSearch,
    ordering,
    onSort,
    onSearchChange,
    onPageChange,
    setTotalCount,
    resetPage,
  } = useTableState({
    defaultSortField: 'created_ts',
    defaultSortDir: 'desc',
    pageSize: PAGE_SIZE,
    searchDebounceMs: 400,
  });

  // The reason catalogue is static on the backend, so it is fetched once.
  useEffect(() => {
    let cancelled = false;
    orderGenerationFailuresApi.getReasonCategories()
      .then(categories => { if (!cancelled) setReasonOptions(categories); })
      .catch(() => { if (!cancelled) setReasonOptions([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!dateFrom && !dateTo) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
        const response = await orderGenerationFailuresApi.list({
          page: currentPage,
          page_size: PAGE_SIZE,
          search: debouncedSearch.trim() || undefined,
          ...getClientFilter(clientVal),
          company: selectedCompany ? parseInt(selectedCompany.value) : undefined,
          order_type: selectedOrderType ? parseInt(selectedOrderType.value) : undefined,
          reason_category: selectedReason?.value || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          ordering,
        });
        if (!cancelled) {
          setFailures(response.results);
          setTotalCount(response.count);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load order generation failures');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentPage, selectedClient, selectedCompany, selectedOrderType, selectedReason, dateFrom, dateTo, debouncedSearch, ordering, setTotalCount]);

  const hasActiveFilters = !!(selectedClient || selectedCompany || selectedOrderType || selectedReason);

  const clearFilters = () => {
    setSelectedClient(null);
    setSelectedCompany(null);
    setSelectedOrderType(null);
    setSelectedReason(null);
    resetPage();
  };

  const filterSlot = (
    <div className={styles['filter-row']}>
      <DateRangeFilter
        startDate={dateFrom}
        endDate={dateTo}
        onStartDateChange={date => { setDateFrom(date); resetPage(); }}
        onEndDateChange={date => { setDateTo(date); resetPage(); }}
      />

      <FilterAsyncSelect
        label="Client"
        value={selectedClient}
        onChange={opt => { setSelectedClient(opt); resetPage(); }}
        loadOptions={loadClientOptions}
        placeholder="All Clients"
      />

      <FilterAsyncSelect
        label="Company"
        value={selectedCompany}
        onChange={opt => { setSelectedCompany(opt); resetPage(); }}
        loadOptions={loadCompanyOptions}
        placeholder="All Companies"
      />

      <FilterSelect
        label="Order Type"
        value={selectedOrderType}
        onChange={opt => { setSelectedOrderType(opt); resetPage(); }}
        options={ORDER_TYPE_OPTIONS}
        placeholder="All Types"
      />

      <FilterSelect
        label="Failure Reason"
        value={selectedReason}
        onChange={opt => { setSelectedReason(opt); resetPage(); }}
        options={reasonOptions}
        placeholder="All Reasons"
      />
    </div>
  );

  return (
    <DataTable
      title="Order Generation Failures"
      subtitle="Orders the generator could not raise, with the reason for each."
      data={failures}
      columns={columns}
      rowKey={row => row.id}
      totalCount={totalCount}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      pageSize={PAGE_SIZE}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search by client code, name, or reason…"
      filterSlot={filterSlot}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={clearFilters}
      emptyMessage="No order generation failures found for the selected filters."
    />
  );
};

export default OrderGenerationFailures;
